import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import {
  DWSIntegration,
  DWSIntegrationConnector,
  IntegrationDetail,
  WineryIntegration,
} from "@interfaces/dws/integration";
import { Login } from "@services/accounts.service";
import { WineryIntegrationsService } from "@services/dws/winery-integrations.service";
import { DWSLanguagesService } from "@services/dws";
import { access } from "@util/route/access";
import { tr } from "@util/tr";
import { AddEditIntegrationComponent } from "@web/app/components/dialogs/add-edit-integration/add-edit-integration.component";
import { observe } from "@web/util/loading/loading";
import { toast } from "@web/util/toast";
import { WsChipType } from "@web/util/ws-ui/ws-chip/ws-chip.component";

@Component({
  selector: "web-integration-detail",
  templateUrl: "./integration-detail.component.html",
  styleUrls: ["./integration-detail.component.scss"],
})
export class IntegrationDetailComponent implements OnInit {
  integration: DWSIntegrationConnector;
  details: IntegrationDetail[] = [];
  login: Login;
  wineryIntegration?: WineryIntegration;

  lang!: string;
  readonly WsChipType = WsChipType;
  integrationDescriptionHtml: SafeHtml = "";
  htmlDetails: { type: string; htmlValue: SafeHtml }[] = [];

  get isSuperadmin(): boolean {
    return !!this.login?.authorization?.is_admin;
  }

  get isAvailableForMyPlan(): boolean {
    let level = this.integration.integration?.requiredLevel ?? 0;
    if (level == 0 || this.login.authorization.is_admin) return true;
    let pro = this.login.company.dws_pro_enabled;
    if (pro) return level <= 2;
    else return level <= 1;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private wineryIntegrationsService: WineryIntegrationsService,
    private dwsLanguagesService: DWSLanguagesService
  ) {
    this.integration = access(route).data("integration");
    this.details = access(route).data("integrationDetails");
    this.login = access(route).data("login");
    this.lang = this.dwsLanguagesService.getCurrentLanguageIso();
    this.refreshSanitizedHtml();
  }

  private refreshSanitizedHtml(): void {
    if (this.integration?.integration) {
      const raw = this.getIntegrationDescription(this.integration.integration) || "";
      this.integrationDescriptionHtml = this.sanitizer.bypassSecurityTrustHtml(
        this.integrationDescriptionFromEncodedText(raw)
      );
    }
    this.htmlDetails = this.details
      .filter((detail) => detail.htmlValue)
      .map((detail) => ({
        type: detail.type,
        htmlValue: this.sanitizer.bypassSecurityTrustHtml(
          this.integrationDescriptionFromEncodedText(detail.htmlValue!)
        ),
      }));
  }

  getIntegrationDescription(integration: any): string {
    let translations = integration.disclaimerTranslations;
    if (typeof translations === "string") {
      try {
        translations = JSON.parse(translations.trim());
      } catch {
        translations = undefined;
      }
    }

    if (!translations || typeof translations !== "object") {
      return integration.description || integration.disclaimer || "";
    }

    if (translations[this.lang]) {
      return translations[this.lang];
    }

    if (translations["en"]) {
      return translations["en"];
    }

    if (translations["it"]) {
      return translations["it"];
    }

    const availableKeys = Object.keys(translations);
    if (availableKeys.length > 0) {
      return translations[availableKeys[0]];
    }

    return integration.description || integration.disclaimer || "";
  }

  /**
   * CMS/API sometimes stores markup with angle brackets escaped (&lt;p …&gt;)
   * or double-escaped (&amp;lt;…). Decode so [innerHTML] receives real HTML.
   */
  private integrationDescriptionFromEncodedText(s: string): string {
    if (!s) {
      return s;
    }
    if (!/&lt;|&amp;(?:lt|gt|amp|quot|#\d+|#x[0-9a-f]+);/i.test(s)) {
      return s;
    }
    const textarea = document.createElement("textarea");
    let current = s;
    for (let i = 0; i < 4; i++) {
      textarea.innerHTML = current;
      const next = textarea.value;
      if (next === current) {
        break;
      }
      current = next;
      if (!/&lt;|&amp;(?:lt|gt);/i.test(current)) {
        break;
      }
    }
    return current;
  }

  getIntegrationDescriptionHtml(integration: any): SafeHtml {
    const raw = this.getIntegrationDescription(integration) || "";
    return this.sanitizer.bypassSecurityTrustHtml(
      this.integrationDescriptionFromEncodedText(raw)
    );
  }

  ngOnInit() {
    if (this.isStripeIntegration() && !this.isSuperadmin) {
      this.router.navigate(["/integrations"]);
      return;
    }
    this.loadWineryIntegration();
  }

  isWhatsAppIntegration(): boolean {
    const integration = this.integration.integration;
    const code = integration?.code?.toLowerCase();
    const name = integration?.name?.toLowerCase();
    const integrationName = this.integration.integrationName?.toLowerCase();
    return (
      code === "whatsapp" ||
      name === "whatsapp" ||
      integrationName === "whatsapp"
    );
  }

  toggleActiveState(integration: DWSIntegration, active: boolean) {
    this.updateIntegrationOrOutputError(
      {
        wineryId: this.login.company.id,
        integrationId: integration.id,
        integration: integration,
      } as WineryIntegration,
      active
    );
  }

  isConnected(): boolean {
    return this.wineryIntegration?.connected ?? false;
  }

  isInstalledInThisWinery(): boolean {
    return !!this.wineryIntegration;
  }

  isEnabledInThisWinery(): boolean {
    return !!this.wineryIntegration && !this.wineryIntegration.disabled;
  }

  isDwspayIntegration(): boolean {
    return this.integration.integration?.code === 'dwspay';
  }

  isStripeIntegration(): boolean {
    return this.integration.integration?.code === 'stripe';
  }

  isFiscalityIntegration(): boolean {
    const code = this.integration.integration?.code?.toLowerCase();
    return code === 'fiskaly' || code === 'wsfiscality' || code === 'fiscality';
  }

  isOnboardingStartedButNotCompleted(): boolean {
    // IMPORTANT: Check if onboarding was never completed (not just disabled by user)
    // - onboarding_completed=true + disabled=true → user manually disabled (show "Edit")
    // - onboarding_completed=false/missing + disabled=true → never completed (show "Resume Setup")
    // This is only for dwspay. Stripe uses the regular Edit button.
    if (!this.isDwspayIntegration() || !this.wineryIntegration) {
      return false;
    }
    
    const isDisabled = this.wineryIntegration.disabled === true;
    const onboardingCompleted = this.wineryIntegration.integrationData?.['onboarding_completed'] === true;
    
    // Show "Resume Setup" only if disabled AND onboarding was never completed
    return isDisabled && !onboardingCompleted;
  }

  getResumeSetupTooltip(): string {
    if (!this.isAvailableForMyPlan) {
      return tr("Integration not included in your plan, please contact the sales team.");
    }
    if (this.isFiscalityIntegration()) {
      return tr("Manage your WineSuite Fiscality settings");
    }
    return tr("Complete your wspayment account setup");
  }

  getInstallTooltip(): string | null {
    if (!this.isAvailableForMyPlan) {
      return tr("Integration not included in your plan, please contact the sales team.");
    }
    return null;
  }

  installOrManage() {
    // For dwspay integration, always navigate to payments page (setup or edit)
    if (this.isDwspayIntegration()) {
      this.router.navigate(['/payments']);
      return;
    }
    
    // For fiscality integration, navigate to fiscality (receipts) page
    if (this.isFiscalityIntegration()) {
      this.router.navigate(['/fiscality']);
      return;
    }

    // For other integrations, open the dialog
    this.dialog
      .open(AddEditIntegrationComponent, {
        minWidth: 1000,
        panelClass: "no-overflow",
        data: {
          wineryId: this.wineryIntegration?.wineryId,
          integration: this.integration,
          wineryIntegration: this.wineryIntegration,
        },
      })
      .afterClosed()
      .subscribe((data) => {
        this.loadWineryIntegration();
      });
  }

  removeDwspayIntegration() {
    if (!this.wineryIntegration) {
      return;
    }

    // Confirm before removing
    if (!confirm(tr("Are you sure you want to remove this integration? This action cannot be undone."))) {
      return;
    }

    observe(
      this.wineryIntegrationsService.disconnectIntegration(
        this.login.company.id,
        this.wineryIntegration
      )
    ).subscribe({
      next: () => {
        toast(tr("Integration removed successfully"));
        this.loadWineryIntegration();
      },
      error: (err) => {
        console.error("Error removing integration:", err);
        toast(tr("An error occurred. Try later"));
        this.loadWineryIntegration();
      },
    });
  }

  updateIntegrationOrOutputError(
    integration: WineryIntegration,
    active: boolean
  ) {
    if (active) {
      observe(
        this.wineryIntegrationsService.connectIntegration(
          this.login.company.id,
          integration
        )
      ).subscribe({
        next: () => {
          toast(tr("Integration saved successfully"));
        },
        error: (err) => {
          console.error("Error updating integration:", err);
          toast(tr("An error occurred. Try later"));
          this.loadWineryIntegration();
        },
        complete: () => {
          this.loadWineryIntegration();
        },
      });
    } else {
      observe(
        this.wineryIntegrationsService.disconnectIntegration(
          this.login.company.id,
          integration
        )
      ).subscribe({
        next: () => toast(tr("Integration saved successfully")),
        error: (err) => {
          console.error("Error updating integration:", err);
          toast(tr("An error occurred. Try later"));
          this.loadWineryIntegration();
        },
        complete: () => this.loadWineryIntegration(),
      });
    }
  }

  loadWineryIntegration() {
    observe(
      this.wineryIntegrationsService.getWineryIntegrations(this.login.wineryId)
    ).subscribe((wi) => {
      this.wineryIntegration = wi.find(
        (wi) => wi.integrationId === this.integration.integration?.id
      );
    });
  }

  backClicked() {
    this.router.navigate(["/integrations"]);
  }
}
